import { Component, inject, Input } from '@angular/core';
import { environment } from '../../../../environments/environment';

import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../../services/auth/auth.service';

import {
  CompetitionFavoritesMatchesByDayService,
  TeamFavoritesMatchesByDayService,
} from '@/app/pages/favorites/services';
import { FootballMatchesService } from '@/app/pages/sports/football/home/services';
import {
  CompetitionFavoritesService,
  MatchFavoriteService,
  NewTeamFavoritesService,
} from '@/app/services/favorite';
import { RouteValidatorService } from '@/app/services/route-validator.service';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { ThemeService } from '../../../services/theme.service';

@Component({
  selector: 'app-auth-modal',
  standalone: true,
  imports: [
    FormsModule,
    NzModalModule,
    CommonModule,
    ReactiveFormsModule,
    NzSpinModule,
    NzAlertModule,
    TranslateModule,
  ],
  templateUrl: './auth-modal.component.html',
  styleUrl: './auth-modal.component.scss',
})
export class AuthModalComponent {
  private competitionsFavorites = inject(CompetitionFavoritesService);
  private teamsFavorites = inject(NewTeamFavoritesService);
  private matchesFavorites = inject(MatchFavoriteService);
  private routeValidatorService = inject(RouteValidatorService);
  private soccerMatchesService = inject(FootballMatchesService);
  private competitionFavoritesMatchesByDayService = inject(
    CompetitionFavoritesMatchesByDayService
  );
  private teamFavoritesMatchesByDayService = inject(
    TeamFavoritesMatchesByDayService
  );

  @Input() isRegister = false;

  urlIMG = environment.urlIMG;
  urlIcon = environment.urlIcon;

  loader: boolean = false;
  isMail: string | null = null;
  errorMessage: string | null = null;

  profileForm = new FormGroup({
    email: new FormControl('', [Validators.required, Validators.email]),
    password: new FormControl('', []),
    confirmPassword: new FormControl('', []),
    termsAndConditions: new FormControl(false, [Validators.requiredTrue]),
    privacyPolicy: new FormControl(false, [Validators.requiredTrue]),
    communicationsConsent: new FormControl(false),
    dataProcessingConsent: new FormControl(false),
  });

  constructor(
    public themeService: ThemeService,
    public readonly authServices: AuthService,
    private modal: NzModalRef,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    // console.log('isRegister ===> ', this.isRegister);
    this.isRegister && this.changeMailForm('SIGNUP');
  }

  close(): void {
    this.modal.close();
  }

  passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.get('password')?.value;
    const confirmPassword = control.get('confirmPassword')?.value;
    return password === confirmPassword ? null : { passwordsMismatch: true };
  }

  changeMailForm(type: 'SIGNIN' | 'SIGNUP' | 'FORGOTPASSWORD') {
    this.isMail = type;
    this.errorMessage = null;
    this.profileForm.reset();

    // Update validators based on the form type
    if (type === 'SIGNIN') {
      this.profileForm.controls['password'].setValidators([
        Validators.required,
        Validators.minLength(6),
      ]);
      this.profileForm.controls['termsAndConditions'].clearValidators();
      this.profileForm.controls['privacyPolicy'].clearValidators();

      this.profileForm.clearValidators();
    } else if (type === 'SIGNUP') {
      this.profileForm.controls['password'].setValidators([
        Validators.required,
        Validators.minLength(6),
      ]);
      this.profileForm.controls['termsAndConditions'].setValidators([
        Validators.requiredTrue,
      ]);
      this.profileForm.controls['privacyPolicy'].setValidators([
        Validators.requiredTrue,
      ]);
      this.profileForm.setValidators([this.passwordMatchValidator]);
    } else if (type === 'FORGOTPASSWORD') {
      this.profileForm.controls['password'].clearValidators();
      this.profileForm.clearValidators();
      this.profileForm.controls['termsAndConditions'].clearValidators();
      this.profileForm.controls['privacyPolicy'].clearValidators();
    }

    // Update validity for all controls
    this.profileForm.controls['password'].updateValueAndValidity();
    this.profileForm.updateValueAndValidity();
  }

  forgotPassword() {
    this.errorMessage = null;
    if (!this.profileForm.valid) {
      this.profileForm.markAllAsTouched();
      return;
    }

    this.loader = true;
    this.authServices
      .sendEmail(this.profileForm.value.email || '')
      .then((result: any) => {
        if (result?.success) {
          this.toastr.info(result?.message, 'Success', {
            timeOut: 4000,
            positionClass: 'toast-top-center',
            disableTimeOut: false,
          });
          this.changeMailForm('SIGNIN');
        } else {
          this.errorMessage = result.message;
        }
        this.loader = false;
      });
  }

  register() {
    this.errorMessage = null;
    if (!this.profileForm.valid) {
      this.profileForm.markAllAsTouched();
      return;
    }

    this.loader = true;
    this.authServices
      .register(
        this.profileForm.value.email || '',
        this.profileForm.value.password || ''
      )
      .then((result: any) => {
        if (result.success) {
          console.log('test register');
          //TODO: add script meta
          if ((window as any).fbq) {
            // console.log('📌 fbq disponible, enviando evento...');
            (window as any).fbq('track', 'CompleteRegistration');
          } else {
            // console.warn('❌ fbq no está definido');
          }

          this.toastr.info('Registered Successfully', 'Success', {
            timeOut: 4000,
            positionClass: 'toast-top-center',
            disableTimeOut: false,
          });

          this.callreloadEndpointsWithAuth();

          this.close();
        } else {
          this.errorMessage = result.message;
        }

        this.loader = false;
      });
  }

  signIn(type: string) {
    this.errorMessage = null;
    if (!this.profileForm.valid && type === 'EMAIL') {
      this.profileForm.markAllAsTouched();
      return;
    }

    this.loader = true;
    this.authServices
      .signIn(
        type,
        this.profileForm.value.email || '',
        this.profileForm.value.password || ''
      )
      .then((result: any) => {
        if (result.success) {
          this.toastr.info('Signed in Successfully', 'Success', {
            timeOut: 4000,
            positionClass: 'toast-top-center',
            disableTimeOut: false,
          });

          this.callreloadEndpointsWithAuth();
          this.close();
        } else {
          this.errorMessage = result.message;
        }

        this.loader = false;
      });
  }

  get f() {
    return this.profileForm.controls;
  }

  get formErrors() {
    return this.profileForm.errors;
  }

  private callreloadEndpointsWithAuth() {
    // call endpoint favorites
    this.competitionsFavorites.getFavoritesCompetition();
    this.teamsFavorites.getFavoritesTeams();
    this.matchesFavorites.getAllFavoritesMatches();

    if (this.routeValidatorService.isFutbolSelected()) {
      this.soccerMatchesService.callEndpintByDateClick();
    }

    if (this.routeValidatorService.isFavoritePage()) {
      if (this.routeValidatorService.isActiveTabCompetitionFavorite()) {
        this.competitionFavoritesMatchesByDayService.callEndpintByDateClick();
      } else {
        this.teamFavoritesMatchesByDayService.callEndpintByDateClick();
      }
    }
  }
}
